import {
  type CalculationOutput,
  type CalculationOutputCategory,
} from '../types';

const exponent = Math.pow(10, 2);

const addViaPence = (a: number, b: number): number => {
  return (Math.round(a * exponent) + Math.round(b * exponent)) / exponent;
};

/**
 * multiply two float values, presumed to be a currency value, by reducing it
 * to it's non decimalized value. This should prevent inaccuracies due to float
 * calculations while keeping it's format-appropriate decimal values. It also
 * retains the type, taking `number` arguments and returning a `number`
 *
 * Hard-coded to an exponent of 10^2 since we're only ever working with pence.
 *
 * @param {number} value - currency value to multiply
 * @param {number} mult - multiplier
 * @returns {number}
 */
const multViaPence = (value: number, mult: number): number => {
  return (Math.round(value * exponent) * mult) / exponent;
};

/**
 * Here we can calculate earnings by first converting the presumed
 * currency value to an integer using the currency decimal count as
 * the `exponent`, multiplying the new input value by the discount,
 * and finally adding to an optionally provided existing value.
 * Finally we divide by the exponent again, returning a Float.
 *
 * Ultimately this should prevent float calculation issues and keep
 * the resulting numbers as accurate as possible without resorting to
 * string manipulation methods.
 *
 * existing value is defaulted to 0, if we only want to multiply a
 * value by it's discount.
 *
 * @param {number} spend - spend value, for which we want to apply a percentage
 * discount
 * @param {number} discount - percentage discount, represented as a float
 * @param {number} [existingValue=0] - existing earnings value, which we
 * optionally may want to sum with
 * @returns {number}
 */
const calcEarnings = (
  spend: number,
  discount: number,
  existingValue: number = 0
): number => {
  const calculatedValue = spend * exponent * discount;
  const calculatedExisting = existingValue * exponent;
  return Math.round(calculatedValue + calculatedExisting) / exponent;
};

const applyMonthlyFee = (earnings: number, fee: number): number =>
  Math.max(0, addViaPence(earnings, -fee));

const createCalcOutput = (
  categories: CalculationOutputCategory[]
): CalculationOutput => ({
  categories,
  total: {
    earnings: categories.reduce<number>(
      (acc, category) => addViaPence(acc, category.earnings),
      0
    ),
    spend: categories.reduce<number>(
      (acc, category) => acc + Number(category.spend),
      0
    ),
  },
});

export {
  createCalcOutput,
  calcEarnings,
  multViaPence,
  applyMonthlyFee,
  addViaPence,
};
